/*
Parse an XHR returned from the Livewire API containing error JSON of the
form:
{ 'key': [ 'error 1', 'error 2' ] }
into the form:
{
 'key': { error1: {'message': 'error 1'}, error2: {'message':'error 2' }},
 'getErrorSummary': () => 'Key: error 1, error 2.'
 'anyUnhandledErrors': () => true
}
which is easier to process for specific errors. If the error is a string
it gets parsed into the form:
{
  'key': { 'error': {'message': 'Error from XHR'}},
  'getErrorSummary': () => 'Key: error: Error from XHR',

}

`getErrorSummary` is a function that returns an error summary, skipping any
 errors that have been marked as handled via
`errors.email.hasAlreadyBeenTaken.handled = true`, so if you handle errors
say in the validation results of a form, mark them has handled, then use a
more generic way of displaying the remaining error summary (such as in an
alert).

`anyUnhandledErrors` is a function that returns true if there are any
unhandled errors.
*/
import _ from 'lodash';

export default function parseFailXhr(xhr) {
  const json = typeof xhr.responseJSON === 'object'
    ? xhr.responseJSON
    : {
      error: [
        xhr.responseJSON || xhr.responseText,
      ],
    };
  const result = Object.keys(json)
    .reduce(
      (keyAccumulator, keyValue) => {
        const errors = json[keyValue];
        const uniqueErrors = [...new Set(errors)];
        const errorMap = uniqueErrors.reduce(
          (errorAccumulator, errorValue) => {
            errorAccumulator[_.camelCase(errorValue)] = {
              message: errorValue,
              handled: false,
            };

            return errorAccumulator;
          }, {},
        );
        keyAccumulator[keyValue] = errorMap;

        return keyAccumulator;
      }, {},
    );

  result.getErrorSummary = () => Object.keys(json)
    .reduce(
      (summary, keyValue) => {
        const errorsSummary = Object.keys(result[keyValue])
          .filter(errorKey => !result[keyValue][errorKey].handled)
          .reduce(
            (errorsSummaryAccumulator, errorKey) => {
              const errorMessage = result[keyValue][errorKey].message;

              return errorsSummaryAccumulator ? `${errorsSummaryAccumulator}, ${errorMessage}` : errorMessage;
            }, '',
          );

        if (!errorsSummary) return summary;

        const keySummary = `${_.capitalize(keyValue)}: ${errorsSummary}`;

        return summary ? `${summary}, ${keySummary}` : keySummary;
      }, '',
    );
  result.anyUnhandledErrors = () => Object.keys(json)
    .filter(x => x !== 'getErrorSummary')
    .reduce(
      (state, keyValue) => {
        if (state) return state;
        return !!Object.keys(result[keyValue])
          .filter(errorKey => !result[keyValue][errorKey].handled)
          .length;
      }, false,
    );

  return result;
}
